import { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, Loader2, FileText, Image as ImageIcon, RotateCcw, X } from 'lucide-react';
import {
  getBatchStatus,
  BatchStatus,
  reprocessAnnouncement,
  archiveFailedAnnouncement,
} from '../lib/api/announcements.js';

interface BatchProgressCardProps {
  batchId: string;
  onComplete?: (status: BatchStatus) => void;
  onClose?: () => void;
}

const POLL_INTERVAL_MS = 4000;

const TERMINAL_PROCESSING_STATUSES = new Set(['completed', 'failed', 'archived']);

function batchIsTerminal(status: BatchStatus): boolean {
  if (['completed', 'failed'].includes(status.batch.status)) return true;
  // No batch-level terminal status yet — fall back to all announcements terminal
  // AND we either have an article_id (combined) or all announcements done (independent).
  const allDone = status.announcements.every(a =>
    TERMINAL_PROCESSING_STATUSES.has(a.processing_status)
  );
  if (!allDone) return false;
  if (status.batch.batch_mode === 'combined') {
    return Boolean(status.article_id);
  }
  return true;
}

export function BatchProgressCard({ batchId, onComplete, onClose }: BatchProgressCardProps) {
  const [status, setStatus] = useState<BatchStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-item failure actions (2026-08-03): Retry re-runs processing on the
  // stored file; Clear archives the failed stub so the file can be
  // re-uploaded. pollEpoch restarts the poll loop after a retry (the loop
  // stops at terminal state, and a retry leaves it).
  const [clearedIds, setClearedIds] = useState<Set<string>>(new Set());
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [pollEpoch, setPollEpoch] = useState(0);

  const withBusy = async (id: string, fn: () => Promise<void>) => {
    setBusyIds(prev => new Set(prev).add(id));
    try {
      await fn();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Action failed');
    } finally {
      setBusyIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const retryItem = (id: string) =>
    withBusy(id, async () => {
      await reprocessAnnouncement(id);
      setPollEpoch(e => e + 1); // resume polling — item is no longer terminal
    });

  const clearItem = (id: string) =>
    withBusy(id, async () => {
      await archiveFailedAnnouncement(id);
      setClearedIds(prev => new Set(prev).add(id));
    });

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      try {
        const s = await getBatchStatus(batchId);
        if (cancelled) return;
        setStatus(s);
        if (batchIsTerminal(s)) {
          onComplete?.(s);
          return; // stop polling
        }
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || 'Failed to load batch status');
      }
      if (!cancelled) {
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [batchId, onComplete, pollEpoch]);

  if (error) {
    return (
      <div className="bg-surface rounded-lg shadow-sm border border-red-200 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <div className="flex-1">
            <h3 className="font-semibold text-grey-900">Batch status error</h3>
            <p className="text-sm text-red-600">{error}</p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-sm text-grey-500 hover:text-grey-700 font-medium"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="bg-surface rounded-lg shadow-sm border border-grey-200 p-6">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
          <p className="text-grey-900 font-semibold">Loading batch status…</p>
        </div>
      </div>
    );
  }

  const terminal = batchIsTerminal(status);
  const visible = status.announcements.filter(a => !clearedIds.has(a.announcement_id));
  const total = visible.length;
  const completed = visible.filter(a => a.processing_status === 'completed').length;
  const failed = visible.filter(a => a.processing_status === 'failed').length;

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-grey-200 p-6">
      <div className="flex items-start gap-3">
        {terminal ? (
          <CheckCircle className="h-5 w-5 text-green-600" />
        ) : (
          <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="font-semibold text-grey-900 truncate">
              {status.batch.batch_label || `Batch ${status.batch.batch_id.slice(0, 8)}`}
            </h3>
            <span className="text-xs text-grey-500 uppercase tracking-wide">
              {status.batch.batch_mode}
            </span>
          </div>
          <p className="text-sm text-grey-600 mt-1">
            {completed}/{total} completed
            {failed > 0 && ` · ${failed} failed`}
            {status.article_id && (
              <span className="ml-2 text-green-700">
                · Article generated
              </span>
            )}
          </p>

          <ul className="mt-3 space-y-1">
            {visible.map(a => (
              <li key={a.announcement_id} className="flex items-center gap-2 text-sm">
                {a.file_type === 'pdf' ? (
                  <FileText className="h-3.5 w-3.5 text-grey-400" />
                ) : (
                  <FileText className="h-3.5 w-3.5 text-blue-400" />
                )}
                <span className="flex-1 truncate text-grey-700">
                  {a.original_filename}
                </span>
                <span className="text-xs text-grey-500 uppercase">
                  {a.source_type === 'announcement' ? '' : a.source_type}
                </span>
                <span className={`text-xs ${
                  a.processing_status === 'completed' ? 'text-green-700' :
                  a.processing_status === 'failed' ? 'text-red-600' :
                  'text-blue-600'
                }`}>
                  {a.processing_status}
                </span>
                {a.processing_status === 'failed' && (
                  <span className="flex items-center gap-1">
                    <button
                      onClick={() => retryItem(a.announcement_id)}
                      disabled={busyIds.has(a.announcement_id)}
                      title={`Retry processing with the same stored file${a.error_message ? `\n\nFailure: ${a.error_message}` : ''}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-blue-700 border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-50"
                    >
                      {busyIds.has(a.announcement_id)
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <RotateCcw className="h-3 w-3" />}
                      Retry
                    </button>
                    <button
                      onClick={() => clearItem(a.announcement_id)}
                      disabled={busyIds.has(a.announcement_id)}
                      title="Archive this failed item so the same file can be re-uploaded"
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-grey-600 border border-grey-200 rounded hover:bg-grey-50 disabled:opacity-50"
                    >
                      <X className="h-3 w-3" />
                      Clear
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>

          {visible.some(a => a.processing_status === 'failed' && a.error_message) && (
            <div className="mt-2 space-y-1">
              {visible
                .filter(a => a.processing_status === 'failed' && a.error_message)
                .map(a => (
                  <p key={a.announcement_id} className="text-xs text-red-700 truncate" title={a.error_message ?? undefined}>
                    {a.original_filename}: {a.error_message}
                  </p>
                ))}
            </div>
          )}

          {status.article_id && (
            <a
              href={`/articles/${status.article_id}`}
              className="mt-3 inline-block text-sm font-semibold text-blue-700 hover:underline"
            >
              View article →
            </a>
          )}
        </div>

        {terminal && onClose && (
          <button
            onClick={onClose}
            className="text-sm text-grey-500 hover:text-grey-700 font-medium"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}
